import { useEffect, useState } from 'react';
import { liveChatAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Copy, Check, Code, ExternalLink, Eye, Loader2, MessageSquare, Sparkles, Play } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const EmbedWidgetPage = () => {
  const { hasRole } = useAuth();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copiedScript, setCopiedScript] = useState(false);
  const [copiedIframe, setCopiedIframe] = useState(false);
  const [previewDomain, setPreviewDomain] = useState('https://example.com');
  const [testDialogOpen, setTestDialogOpen] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await liveChatAPI.getSettings();
      setSettings(res.data);
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Generate the script tag embed code
  const scriptEmbedCode = `<!-- UnifiCRM Live Chat Widget -->
<script>
  (function() {
    var w = window;
    var d = document;
    var s = d.createElement('script');
    s.src = '${API_URL}/api/chat-widget.js';
    s.async = true;
    s.onload = function() {
      if (w.UnifiChat) {
        w.UnifiChat.init({
          apiUrl: '${API_URL}'
        });
      }
    };
    d.head.appendChild(s);
  })();
</script>`;

  // Generate the iframe embed code
  const iframeEmbedCode = `<!-- UnifiCRM Live Chat Widget (iframe) -->
<iframe
  src="${API_URL}/chat-widget"
  style="position: fixed; bottom: 20px; right: 20px; width: 400px; height: 600px; border: none; z-index: 9999; border-radius: 16px; box-shadow: 0 10px 40px rgba(0,0,0,0.15);"
  allow="microphone"
  title="Live Chat"
></iframe>`;

  const copyToClipboard = async (text, type) => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'script') {
        setCopiedScript(true);
        setTimeout(() => setCopiedScript(false), 2000);
      } else {
        setCopiedIframe(true);
        setTimeout(() => setCopiedIframe(false), 2000);
      }
      toast.success('Copied to clipboard!');
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  if (!hasRole(['admin'])) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="p-8 text-center">
          <Code className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">Only administrators can access widget embed codes.</p>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="embed-widget-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Code className="h-6 w-6" />
            Embed Chat Widget
          </h1>
          <p className="text-muted-foreground">
            Add the live chat widget to any website with a simple code snippet
          </p>
        </div>
        <Dialog open={testDialogOpen} onOpenChange={setTestDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="test-widget-btn">
              <Play className="h-4 w-4 mr-2" />
              Test Widget
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-4xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Live Widget Preview
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              <p className="text-sm text-muted-foreground mb-4">
                This preview shows how the chat widget will appear and function on your website.
              </p>
              <div className="relative bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg h-[500px] overflow-hidden border">
                {/* Mock website content */}
                <div className="absolute inset-0 p-6">
                  <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                    <div className="h-3 w-32 bg-slate-300 rounded mb-3"></div>
                    <div className="h-2 w-full bg-slate-200 rounded mb-2"></div>
                    <div className="h-2 w-3/4 bg-slate-200 rounded"></div>
                  </div>
                  <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
                    <div className="h-3 w-48 bg-slate-300 rounded mb-3"></div>
                    <div className="h-2 w-full bg-slate-200 rounded mb-2"></div>
                    <div className="h-2 w-full bg-slate-200 rounded mb-2"></div>
                    <div className="h-2 w-2/3 bg-slate-200 rounded"></div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg shadow-sm p-3">
                      <div className="h-16 bg-slate-100 rounded mb-2"></div>
                      <div className="h-2 w-full bg-slate-200 rounded"></div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-3">
                      <div className="h-16 bg-slate-100 rounded mb-2"></div>
                      <div className="h-2 w-full bg-slate-200 rounded"></div>
                    </div>
                    <div className="bg-white rounded-lg shadow-sm p-3">
                      <div className="h-16 bg-slate-100 rounded mb-2"></div>
                      <div className="h-2 w-full bg-slate-200 rounded"></div>
                    </div>
                  </div>
                </div>
                
                {/* Embedded iframe widget */}
                <iframe
                  src={`${API_URL}/chat-widget-embed`}
                  className="absolute bottom-4 right-4 w-[350px] h-[450px] border-none rounded-2xl shadow-2xl"
                  allow="microphone"
                  title="Live Chat Widget Preview"
                />
              </div>
              <div className="mt-4 flex justify-between items-center">
                <p className="text-xs text-muted-foreground">
                  Note: The widget is fully functional in this preview. Try sending a test message!
                </p>
                <Button variant="outline" onClick={() => setTestDialogOpen(false)}>
                  Close Preview
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Status Check */}
      {!settings?.is_enabled && (
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <MessageSquare className="h-5 w-5 text-amber-600 mt-0.5" />
              <div>
                <p className="font-medium text-amber-900">Live chat is currently disabled</p>
                <p className="text-sm text-amber-700">
                  Enable live chat in the{' '}
                  <a href="/live-chat/settings" className="underline font-medium">
                    settings page
                  </a>{' '}
                  before embedding the widget.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Embed Code Section */}
        <div className="lg:col-span-2 space-y-6">
          <Tabs defaultValue="script" className="space-y-4">
            <TabsList>
              <TabsTrigger value="script" data-testid="script-tab">
                <Code className="h-4 w-4 mr-2" />
                Script Tag (Recommended)
              </TabsTrigger>
              <TabsTrigger value="iframe" data-testid="iframe-tab">
                <ExternalLink className="h-4 w-4 mr-2" />
                Iframe
              </TabsTrigger>
            </TabsList>

            <TabsContent value="script">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">JavaScript Embed Code</CardTitle>
                  <CardDescription>
                    Add this script to your website&apos;s HTML, just before the closing &lt;/body&gt; tag.
                    This is the recommended method for the best performance and flexibility.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                      {scriptEmbedCode}
                    </pre>
                    <Button
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(scriptEmbedCode, 'script')}
                      data-testid="copy-script-btn"
                    >
                      {copiedScript ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                    <h4 className="font-medium text-blue-900 flex items-center gap-2 mb-2">
                      <Sparkles className="h-4 w-4" />
                      Benefits of Script Method
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>Loads asynchronously without blocking page load</li>
                      <li>Automatically positions in the corner of your site</li>
                      <li>Responsive and mobile-friendly</li>
                      <li>Inherits your configured colors and settings</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="iframe">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Iframe Embed Code</CardTitle>
                  <CardDescription>
                    Use this iframe if you need more control over positioning or if scripts are restricted on your platform.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="relative">
                    <pre className="bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto text-sm font-mono">
                      {iframeEmbedCode}
                    </pre>
                    <Button
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(iframeEmbedCode, 'iframe')}
                      data-testid="copy-iframe-btn"
                    >
                      {copiedIframe ? (
                        <>
                          <Check className="h-4 w-4 mr-1" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className="h-4 w-4 mr-1" />
                          Copy Code
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4">
                    <h4 className="font-medium text-amber-900 mb-2">Note</h4>
                    <p className="text-sm text-amber-800">
                      The iframe method provides a fixed-position widget. You may need to adjust the 
                      positioning styles to fit your website&apos;s layout.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          {/* Installation Guide */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Installation Guide</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                    1
                  </div>
                  <div>
                    <h4 className="font-medium">Copy the embed code</h4>
                    <p className="text-sm text-muted-foreground">
                      Choose either the script tag or iframe method above and copy the code.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                    2
                  </div>
                  <div>
                    <h4 className="font-medium">Paste into your website</h4>
                    <p className="text-sm text-muted-foreground">
                      For the script method, paste before &lt;/body&gt;. For iframe, paste anywhere in the page body.
                    </p>
                  </div>
                </div>
                <div className="flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-semibold">
                    3
                  </div>
                  <div>
                    <h4 className="font-medium">Test the widget</h4>
                    <p className="text-sm text-muted-foreground">
                      Visit your website and look for the chat bubble in the bottom corner. Click to test!
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Preview Panel */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Widget Preview
              </CardTitle>
              <CardDescription>
                How the chat widget will appear on your website
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="relative bg-gradient-to-br from-slate-100 to-slate-200 rounded-lg h-[400px] overflow-hidden">
                {/* Mock website preview */}
                <div className="absolute inset-0 p-4">
                  <div className="bg-white rounded shadow-sm p-3 mb-3">
                    <div className="h-2 w-24 bg-slate-300 rounded mb-2"></div>
                    <div className="h-2 w-full bg-slate-200 rounded"></div>
                  </div>
                  <div className="bg-white rounded shadow-sm p-3 mb-3">
                    <div className="h-2 w-32 bg-slate-300 rounded mb-2"></div>
                    <div className="h-2 w-full bg-slate-200 rounded mb-1"></div>
                    <div className="h-2 w-3/4 bg-slate-200 rounded"></div>
                  </div>
                  <div className="bg-white rounded shadow-sm p-3">
                    <div className="h-2 w-20 bg-slate-300 rounded mb-2"></div>
                    <div className="h-2 w-full bg-slate-200 rounded"></div>
                  </div>
                </div>

                {/* Chat Widget Preview */}
                <div className="absolute bottom-4 right-4">
                  <div 
                    className="w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white transition-transform hover:scale-105 cursor-pointer"
                    style={{ backgroundColor: settings?.primary_color || '#3b82f6' }}
                  >
                    <MessageSquare className="h-6 w-6" />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Current Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Status</span>
                <span className={`text-sm font-medium ${settings?.is_enabled ? 'text-green-600' : 'text-red-600'}`}>
                  {settings?.is_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Title</span>
                <span className="text-sm font-medium">{settings?.chat_title || 'Chat with us'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Position</span>
                <span className="text-sm font-medium capitalize">{settings?.position?.replace('-', ' ') || 'Bottom Right'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">Primary Color</span>
                <div className="flex items-center gap-2">
                  <div 
                    className="w-5 h-5 rounded border"
                    style={{ backgroundColor: settings?.primary_color || '#3b82f6' }}
                  ></div>
                  <span className="text-sm font-mono">{settings?.primary_color || '#3b82f6'}</span>
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">AI Bot</span>
                <span className={`text-sm font-medium ${settings?.bot_enabled ? 'text-green-600' : 'text-slate-500'}`}>
                  {settings?.bot_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <div className="pt-3 border-t">
                <Button variant="outline" className="w-full" asChild>
                  <a href="/live-chat/settings">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    Modify Settings
                  </a>
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default EmbedWidgetPage;
